import CloseIcon from "@mui/icons-material/Close";
import SettingsIcon from "@mui/icons-material/Settings";
import { Box, Collapse, IconButton, Typography } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import type { AlertSeverity, AlertSeverityRule } from "./types";
import { ALERT_TYPE_COLORS, ALERT_TYPE_I18N_KEYS, ALL_ALERT_TYPES, SEVERITY_COLORS, SEVERITY_I18N_KEYS } from "./utils";

type Props = {
	open: boolean;
	onClose: () => void;
	rules: AlertSeverityRule[];
};

export const SeverityConfigPanel: FC<Props> = ({ open, onClose, rules }) => {
	const { t } = useTranslation();

	return (
		<Collapse in={open} unmountOnExit>
			<Box sx={{ border: "1px solid", borderColor: "grey.200", borderRadius: "8px", p: 2, mb: 2, bgcolor: "white" }}>
				{/* Header */}
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						mb: 2,
					}}
				>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 1,
						}}
					>
						<SettingsIcon sx={{ fontSize: 20, color: "text.secondary" }} />
						<Box>
							<Typography
								variant="subtitle2"
								sx={{
									fontWeight: 700,
								}}
							>
								{t("severity.config")}
							</Typography>
							<Typography
								variant="caption"
								sx={{
									color: "text.secondary",
								}}
							>
								{t("severity.config.description")}
							</Typography>
						</Box>
					</Box>
					<IconButton size="small" onClick={onClose}>
						<CloseIcon fontSize="small" />
					</IconButton>
				</Box>

				{/* Per alert type sections */}
				{ALL_ALERT_TYPES.map((alertType) => {
					const typeColors = ALERT_TYPE_COLORS[alertType];
					const typeRules = rules.filter((r) => r.alertType === alertType);

					return (
						<Box key={alertType} sx={{ bgcolor: "grey.50", borderRadius: "8px", border: "1px solid", borderColor: "grey.200", p: 1.5, mb: 1 }}>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 1,
									mb: typeRules.length > 0 ? 1 : 0,
								}}
							>
								<Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: typeColors.text }} />
								<Typography
									variant="body2"
									sx={{
										fontWeight: 700,
									}}
								>
									{t(ALERT_TYPE_I18N_KEYS[alertType])}
								</Typography>
							</Box>
							{typeRules.map((rule, ruleIdx) => (
								<Box
									key={`${rule.alertType}-${rule.priority}`}
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 1,
										mb: 0.5,
									}}
								>
									<Typography
										variant="caption"
										sx={{
											color: "text.secondary",
											minWidth: 20,
										}}
									>
										{ruleIdx + 1}.
									</Typography>
									<Typography
										variant="caption"
										sx={{
											color: "text.secondary",
										}}
									>
										{t("if")}
									</Typography>
									<Typography
										variant="caption"
										sx={{
											fontWeight: 600,
										}}
									>
										{t(`metric.${rule.metric}`)}
									</Typography>
									<Typography
										variant="caption"
										sx={{
											fontWeight: 600,
										}}
									>
										{rule.operator}
									</Typography>
									<Typography
										variant="caption"
										sx={{
											fontWeight: 600,
										}}
									>
										{rule.threshold}
									</Typography>
									<Typography
										variant="caption"
										sx={{
											color: "text.secondary",
										}}
									>
										{t("then")}
									</Typography>
									<Box
										sx={{
											px: 1,
											py: 0.25,
											borderRadius: "4px",
											bgcolor: SEVERITY_COLORS[rule.severity as AlertSeverity]?.lightBg,
										}}
									>
										<Typography
											variant="caption"
											sx={{
												fontWeight: 600,
											}}
										>
											{t(SEVERITY_I18N_KEYS[rule.severity as AlertSeverity])}
										</Typography>
									</Box>
								</Box>
							))}
							{typeRules.length === 0 && (
								<Typography
									variant="caption"
									sx={{
										color: "text.secondary",
										pl: 3,
									}}
								>
									{t("no.rules")}
								</Typography>
							)}
						</Box>
					);
				})}
			</Box>
		</Collapse>
	);
};
